import { Granularity, StandardPeriod } from "@acto/taskpane/core/domain";
import { isoToUtc } from "@acto/taskpane/field-sales-2/components/primitives/Calendar/dateUtils";

const DAILY_MAX_SPAN_DAYS = 13;
const WEEKLY_MAX_SPAN_DAYS = 70;
// ~12 monthly bars: beyond a year of months, switch to quarterly.
const MONTHLY_MAX_SPAN_DAYS = 366;
// ~12 quarterly bars (~3 years): beyond, switch to yearly.
const QUARTERLY_MAX_SPAN_DAYS = 1097;
const MS_PER_DAY = 86_400_000;

const inclusiveDaySpan = (startIso: string, endIso: string): number =>
  Math.round((isoToUtc(endIso).getTime() - isoToUtc(startIso).getTime()) / MS_PER_DAY) + 1;

const periodSpanDays = (period: StandardPeriod): number => {
  switch (period) {
    case StandardPeriod.LAST_MONTH:
      return 30;
    case StandardPeriod.LAST_3_MONTHS:
      return 91;
    case StandardPeriod.LAST_6_MONTHS:
      return 182;
    case StandardPeriod.LAST_12_MONTHS:
      return 365;
    case StandardPeriod.YEAR_TO_DATE: {
      const now = new Date();
      const jan1 = Date.UTC(now.getUTCFullYear(), 0, 1);
      const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      return Math.round((today - jan1) / MS_PER_DAY) + 1;
    }
  }
};

/**
 * Picks the finest granularity that keeps the bar count manageable, coarsening one step each time
 * the bar count would exceed ~12: daily up to ~2 weeks, weekly up to ~2 months, monthly up to a
 * year, quarterly up to ~3 years, yearly beyond.
 */
export const selectGranularity = (
  dateRange: { startDate: string; endDate: string } | null | undefined,
  period?: StandardPeriod,
): Granularity => {
  const days = dateRange
    ? inclusiveDaySpan(dateRange.startDate, dateRange.endDate)
    : periodSpanDays(period ?? StandardPeriod.YEAR_TO_DATE);

  if (days <= DAILY_MAX_SPAN_DAYS) return Granularity.DAILY;
  if (days <= WEEKLY_MAX_SPAN_DAYS) return Granularity.WEEKLY;
  if (days <= MONTHLY_MAX_SPAN_DAYS) return Granularity.MONTHLY;
  if (days <= QUARTERLY_MAX_SPAN_DAYS) return Granularity.QUARTERLY;
  return Granularity.YEARLY;
};
